import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import AdSense from '../components/AdSense';

const CONTACT_EMAIL = 'optionview580@gmail.com';

function AuthorAvatar() {
  return (
    <div className="w-full aspect-square rounded-clay bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center overflow-hidden">
      <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-4/5 h-4/5">
        {/* Head */}
        <circle cx="50" cy="34" r="18" fill="#93c5fd" stroke="#3b82f6" strokeWidth="1.5"/>
        {/* Glasses */}
        <rect x="30" y="31" width="14" height="9" rx="4" fill="none" stroke="#1d4ed8" strokeWidth="1.8"/>
        <rect x="56" y="31" width="14" height="9" rx="4" fill="none" stroke="#1d4ed8" strokeWidth="1.8"/>
        <line x1="44" y1="35.5" x2="56" y2="35.5" stroke="#1d4ed8" strokeWidth="1.8"/>
        {/* Body / shoulders */}
        <path d="M20 95 Q20 68 50 68 Q80 68 80 95" fill="#bfdbfe" stroke="#3b82f6" strokeWidth="1.5"/>
      </svg>
    </div>
  );
}

const CARD = 'bg-surface border border-surface rounded-clay shadow-clay dark:shadow-clay-dark p-5';

export default function About() {
  return (
    <>
      <SEOHead
        title="About OptionView — Free Options Analysis Tools for Indian Traders"
        description="Learn about OptionView — a free toolkit for Indian options traders with IV Calculator, Black-Scholes Greeks, PDE decomposition and educational blog. Built to democratise options analytics for NIFTY, Bank Nifty and F&O stocks."
        canonicalPath="/about"
        keywords="about OptionView, options trading tools India, implied volatility India, Black-Scholes education, NSE options analytics"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <Link to="/" className="inline-flex items-center gap-1 text-xs text-neutral-400 dark:text-neutral-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors mb-3">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
          Back to Home
        </Link>
        <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100 mb-4">About OptionView</h1>

        <div className="space-y-3 text-neutral-700 dark:text-neutral-300 text-sm sm:text-base">

          {/* ── Section 1: Who I am ─────────────────────────────────────────── */}
          <section className={`${CARD} overflow-hidden`}>
            {/* float-left photo — text wraps around it */}
            <div className="float-left mr-5 mb-2 w-32 sm:w-44 shrink-0">
              <AuthorAvatar />
            </div>
            <p className="mb-3">
              I'm the developer of OptionView, a product manager, and a wannabe options trader. In my attempts to make sense of my trades aren't working, especially the option ones, I came across option greeks. Needless to say, it was all latin and greek to me, and so I decided to start from the beginning. I have always been interested in mathematical modelling, and theoretical approaches, but never really entered the pool, let alone the deep end. I figured it was time, and I started looking at the Black Scholes equation. 
            </p>
            <p className="mb-3">
              This project is just that, two serve as a notebook where I try and write down what I understand about the equation, and hopefully see how it works out in reality. The tools I’m making and putting up come out as a result of understanding certain aspects of this whole Black Scholes business.
            </p>
            <p className="mb-3">
              Given that I’m an absolute beginner, I’d love to get your feedback on anything that I’m wrong on, or any feature/tool that you’d like to see, and I will make an attempt to first explain, and then put out the relevant tool. So, feedback is welcome (through that small widget on the bottom right). Feel free to write to me directly on the email below. 
            </p>
            <p className="mb-3">
              Another disclaimer, since this is a POC, the background data is being sourced only for a daily level, and from multiple free sources that exist in India, so price and calculation accuracy is not guaranteed. Please do not use this tool as a way to make actual trades, me, the tool and the blogs are just my way of interpreting the equations and/or strategies. Please refer to the Disclaimers Page here for more detail on this.
            </p>
            <p className="mb-3">
              Happy Learning!
            </p>
            <p className="mb-3">
              The developer of OptionView
            </p>
  
            <p>
              P.S.: I'm always happy to hear from other traders — bugs, feature ideas, or just to
              talk options. Reach me at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                {CONTACT_EMAIL}
              </a>.
            </p>
            <div className="clear-both" />
          </section>

          {/* ── Section 2: What felt was missing ────────────────────────────── */}
          <section className={CARD}>
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">What I felt was missing</h2>
            <p className="mb-2">
              Although greeks and volatility metrics are present in most retail trading platforms, they are just that, metrics.
              For beginner to intermediate traders, I believe an increased understanding of how options work under the hood would help in better decision making.
              That is at least what I intend to do for myself, and why this toolkit started.
            </p>
          </section>

          {/* ── How It Works ────────────────────────────────────────────────── */}
          <section className={CARD}>
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">How It Works</h2>
            <p className="mb-2">
              Every trading day sometime at 9:30 PM IST, OptionView automatically downloads the NSE
              Bhavcopy file, and from a few other open sources. I'd want to see how this tool goes, and then
              give credit to the data provider :)
              What happens:
            </p>
            <ol className="list-decimal pl-5 space-y-1 mt-2">
              <li>Identifies the nearest weekly (or monthly) expiry option</li>
              <li>Finds the at-the-money (ATM) strike based on the spot price</li>
              <li>Calculates IV for the ATM call and put using the Black-Scholes model</li>
              <li>Averages CE and PE implied volatilities for a single IV figure</li>
              <li>Computes IV Rank (52-week) and IV Percentile (90-day)</li>
              <li>Stores the result and serves it via API</li>
            </ol>
            <p className="mt-2">
              The frontend retrieves this data and displays it with a 90-day historical chart.
              No manual data entry required — everything is automated.
            </p>
          </section>

          {/* ── Future Plans ─────────────────────────────────────────────────── */}
          <section className={CARD}>
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">Future Plans</h2>
            <p className="mb-2">
                This site is intended as a toolkit for myself, and as an educational/informative guide for others.
                I intend to continue to write, build and update new tools here as I learn more. Below is a <strong>short</strong>
                list of tools I have in my backlog
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Options Greeks calculator (Delta, Gamma, Theta, Vega)</li>
              <li>Position sizing and risk calculator</li>
              <li>Max Pain analysis for near expiry dates</li>
              <li>...</li>
            </ul>
          </section>

          {/* ── Get in Touch ─────────────────────────────────────────────────── */}
          <section className={CARD}>
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">Get in Touch</h2>
            <p className="mb-4">
              Bug reports, feature requests, feedback, or just to say hello —{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                {CONTACT_EMAIL}
              </a>.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-1 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-clay-sm hover:bg-primary-700 transition-colors shadow-clay-btn"
            >
              Contact →
            </Link>
          </section>

        </div>

        <AdSense adSlot="" label="About Page Banner" adStyle={{ minHeight: 90, marginTop: 24 }} />
      </div>
    </>
  );
}
